// src/services/authService.ts
import axios from "axios";
import { API_BASE_URL, AUTH_BRIDGE } from "../config";
import { asErr } from "../utils/apiHelpers";

// ------------------------------------------------------------
// Environment helper
// Logs must NEVER appear in production builds
// ------------------------------------------------------------
const isDev = process.env.NODE_ENV === "development";

// Use relative URLs in development (proxy) or absolute in production
const getBaseURL = () => {
  if (process.env.NODE_ENV === "development") return "";
  return API_BASE_URL;
};

// -------------------------------
// Token helpers (SINGLE source of truth)
// -------------------------------
const TOKEN_KEY = "authToken";

export const setAuthToken = (t: string) => {
  localStorage.setItem(TOKEN_KEY, t);
  logoutFired = false;
  notifyAuthStateChanged();
};

/** Notify listeners (e.g. WP bridge) that auth state changed — call after login/signup */
export function notifyAuthStateChanged() {
  window.dispatchEvent(new CustomEvent(AUTH_BRIDGE.authChangedEventName));
}
export const getAuthToken = () => localStorage.getItem(TOKEN_KEY);
export const clearAuthToken = () => {
  // Clear the single auth token key
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem("authUser");
  // Ensure no login-related cache keys survive logout
  // (e.g. old OTP cooldown keys from previous iterations)
  try {
    const prefix = "benefaction.otpCooldownUntil:";
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) localStorage.removeItem(k);
    }
  } catch {
    // ignore
  }
};
export const isAuthenticated = () => !!getAuthToken();

/** Decode JWT payload safely (for donor identity matching). */
export function decodeJwtPayload(token?: string | null): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const [, payload] = token.split(".");
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decodeURIComponent(escape(json))) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Identity from current JWT for matching donor to logged-in user. */
export function getIdentityFromToken(): { nationalId?: string; phone?: string } | null {
  const token = getAuthToken();
  const p = decodeJwtPayload(token);
  if (!p) return null;
  const loginKind = (p.login_kind as string)?.toString?.()?.toLowerCase?.();
  const loginValue = (p.login_value as string)?.toString?.()?.trim?.();
  const nationalId =
    (p.nationalId ?? p.national_id ?? p.nationalID ?? p.identity_number ?? (loginKind === "nationalid" ? loginValue : null)) as string | undefined;
  const phone = (p.phone ?? p.phone_number ?? p.mobile ?? p.sub ?? (loginKind === "phone" ? loginValue : null)) as string | undefined;
  const out = { nationalId: nationalId?.toString?.()?.trim?.(), phone: phone?.toString?.()?.trim?.() };
  return out.nationalId || out.phone ? out : null;
}

// -------------------------------
// Axios client for AUTH endpoints
// -------------------------------
const http = axios.create({
  baseURL: getBaseURL(),
  headers: { Accept: "application/json" },
  withCredentials: false,
  validateStatus: (s) => s >= 200 && s < 300,
  timeout: 60000,
});

// 🔥 IMPORTANT: stop infinite flicker loops
// We clear token only once and broadcast logout once.
let logoutFired = false;
function fireLogoutOnce(reason?: string) {
  if (logoutFired) return;
  logoutFired = true;

  // kill the local “logged-in” signal
  clearAuthToken();

  // broadcast so UI can redirect ONCE (if you have a listener)
  window.dispatchEvent(new CustomEvent("auth:logout", { detail: { reason } }));
}

// Public endpoints: do NOT send Authorization (avoids AUTH_401 when user has expired token during registration)
const PUBLIC_AUTH_PATHS = [
  "/api/auth/continue/mint",
  "/api/auth/otp/start",
  "/api/auth/signup/checkuser",
  "/api/auth/signup/initiate-otp", // signup challenge OTP
  "/api/auth/donation/login",
];
const isPublicAuthEndpoint = (url?: string) =>
  url && PUBLIC_AUTH_PATHS.some((p) => url.includes(p));

// ✅ Attach Authorization only for non-public endpoints
http.interceptors.request.use((config) => {
  const tok = getAuthToken();

  // DEV-ONLY logging
  // In production, auth-related console logs are intentionally disabled
  if (isDev) {
    console.log("[authService REQ]", config.method?.toUpperCase(), config.url, {
      tokLen: tok?.length ?? 0, // length only — NEVER log token value
      tokenKey: TOKEN_KEY,
    });
  }

  // Skip Authorization for public registration/login endpoints (prevents AUTH_401 from expired token)
  if (tok && !isPublicAuthEndpoint(config.url)) {
    // Axios types: config.headers can be AxiosHeaders | raw object | undefined.
    // Use a safe merge and assign back as 'any' to satisfy TS.
    const headers: any = config.headers ? config.headers : {};
    headers.Authorization = `Bearer ${tok}`;
    config.headers = headers;
  }

  return config;
});

// ✅ Normalize 401 + force stable guest state (prevents flicker)
http.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url as string | undefined;

    // DEV-ONLY error logging
    // Production console must stay clean and silent
    if (isDev) {
      console.log("[authService ERR]", status, url);
    }

    if (status === 401) {
      // If auth endpoints fail with 401, treat it as "not logged in"
      fireLogoutOnce(`401 from ${url ?? "unknown"}`);

      // Normalize error for callers
      const e = new Error("AUTH_401");
      (e as any).code = "AUTH_401";
      throw e;
    }

    throw error;
  }
);

// ---------- Keep: Continue-token mint ----------
export async function mintContinue(
  path: string,
  state?: any
): Promise<{ token: string }> {
  try {
    const { data } = await http.post<{ token: string }>(
      "/api/auth/continue/mint",
      { path, state }
    );
    if (!data || typeof data.token !== "string") throw new Error("Unexpected payload");
    return data;
  } catch (err) {
    throw asErr("mintContinue failed", err);
  }
}



// ---------- OTP start request (matches POST /api/auth/otp/start) ----------
export type OtpStartRequest = {
  identifier?: string | null;
  phone?: string | null;
  captchaToken?: string | null;
  continue?: string | null;
  otpFor?: string | null;
};


// ---------- Signup initiate OTP request (matches POST /api/auth/signup/initiate-otp) ----------
export type SignupInitiateOtpRequest = {
  nationalId: string;
  mobileNumber: string;
  email?: string | null;
};

export type SignupInitiateOtpResponse = {
  challengeId: string;
  maskedMobile?: string | null;
  expiresInSeconds?: number;
};

// ---------- Signup check user (matches POST /api/auth/signup/checkuser) ----------
export type SignupCheckUserRequest = {
  nationalId: string;
  mobileNumber?: string | null;
  email?: string | null;
};

export type SignupCheckUserResponse = {
  success?: boolean;
  message?: string;
};


// ---------- Donation login request (matches POST /api/auth/donation/login) ----------
export type DonationLoginRequest = {
  identifier?: string | null;
  phone?: string | null;
  email?: string | null;
  nationalId?: string | null;
  code: string;
  continueToken?: string | null;
  existingSessionId?: string | null;
};

// ---------- Response DTO ----------
export type DonationLoginResponse = {
  token: string;
  user?: { id: number; name?: string | null; email?: string | null; phone?: string | null } | null;
  donor?: { id: number; name?: string | null; email?: string | null; phone?: string | null } | null;
  continueResult?: { path?: string; state?: any } | null;
};

// ---------- OTP start ----------
export async function otpStart(request: OtpStartRequest) {
  try {
    const body: Record<string, string> = {};
    if (request.identifier != null && request.identifier !== "") body.identifier = request.identifier;
    if (request.phone != null && request.phone !== "") body.phone = request.phone;
    if (request.captchaToken != null && request.captchaToken !== "") body.captchaToken = request.captchaToken;
    if (request.continue != null && request.continue !== "") body.continue = request.continue;
    if (request.otpFor != null && request.otpFor !== "") body.otpFor = request.otpFor;

    const { data } = await http.post("/api/auth/otp/start", body, {
      timeout: 30000, // 15 ثانية — إن لم يرد السيرفر تُظهر الصفحة خطأ بدل التعلّق
    });
    return (data && typeof data === "object" ? data : { ok: true }) as { ok: boolean };
  }  catch (err) {
  throw err;
  }
}


// ---------- Signup initiate OTP ----------
// New challenge-based signup OTP.
// Used by resident/userPartner signup only.
// Keeps old otpStart untouched for old flows.
export async function signupInitiateOtp(
  request: SignupInitiateOtpRequest
): Promise<SignupInitiateOtpResponse> {
  try {
    const body: Record<string, string> = {};

    if (request.nationalId != null && request.nationalId !== "") {
      body.nationalId = request.nationalId;
    }

    if (request.mobileNumber != null && request.mobileNumber !== "") {
      body.mobileNumber = request.mobileNumber;
    }

    if (request.email != null && request.email !== "") {
      body.email = request.email;
    }

    const { data } = await http.post("/api/auth/signup/initiate-otp", body, {
      timeout: 30000,
    });

    return data as SignupInitiateOtpResponse;
  } catch (err) {
    throw err;
  }
}

// ---------- Signup check user ----------
export async function signupCheckUser(
  request: SignupCheckUserRequest
): Promise<SignupCheckUserResponse> {
  try {
    const body: Record<string, string> = {};
    if (request.nationalId != null && request.nationalId !== "") body.nationalId = request.nationalId;
    if (request.mobileNumber != null && request.mobileNumber !== "") body.mobileNumber = request.mobileNumber;
    if (request.email != null && request.email !== "") body.email = request.email;

    // Backend may return 400 with a business message like:
    // "national_id_already_exists,mobile_already_exists,email_already_exists"
    // Treat that as a valid payload so UI can show a friendly message.
    const { data } = await http.post("/api/auth/signup/checkuser", body, {
      timeout: 30000,
      validateStatus: (s) => (s >= 200 && s < 300) || s === 400,
    });
    return (data ?? {}) as SignupCheckUserResponse;
  } catch (err) {
    throw err;
  }
}


// ---------- Donation login ----------
export async function donationLogin(request: DonationLoginRequest): Promise<DonationLoginResponse> {
  try {
    const body: Record<string, string> = {
      code: request.code.trim(),
    };
    if (request.identifier != null && request.identifier !== "") body.identifier = request.identifier;
    if (request.phone != null && request.phone !== "") body.phone = request.phone;
    if (request.email != null && request.email !== "") body.email = request.email;
    if (request.nationalId != null && request.nationalId !== "") body.nationalId = request.nationalId;
    if (request.continueToken != null && request.continueToken !== "") body.continueToken = request.continueToken;
    if (request.existingSessionId != null && request.existingSessionId !== "") body.existingSessionId = request.existingSessionId;

    const { data } = await http.post("/api/auth/donation/login", body);
    const raw = data as Record<string, unknown> | undefined;
    const token = (raw?.token as string) ?? (raw?.Token as string);
    if (!token || typeof token !== "string") throw new Error("Unexpected payload");

    setAuthToken(token);

    const user = raw?.user ?? raw?.User;
    const donor = raw?.donor ?? raw?.Donor;
    const continueResult = raw?.continueResult ?? raw?.ContinueResult;
    // ✅ Save profile for incident report / profile page
    if (user) {
      localStorage.setItem("authUser", JSON.stringify(user));
    }

    return {
      token,
      user: user as DonationLoginResponse["user"],
      donor: donor as DonationLoginResponse["donor"],
      continueResult: continueResult as DonationLoginResponse["continueResult"],
    };
  } catch (err) {
    throw asErr("donationLogin failed", err);
  }
}
